import * as React from 'react';
import { DatasheetItem } from '@modules/datasheets/components/datasheet-item';
import { DatasheetItemLabel } from '@modules/datasheets/components/datasheet-item-label';
import { NumberFieldProps, useNumberFieldController } from './number-field.controller';
import { Flex } from '@common/components/Flex';
import { getHeadingAlignmentStyle, getHeadingPositionOffset } from '../field-heading.utils';
import { FieldContainer } from '../field-container';
import { FormControl } from '@common/components/form-control';
import { Hint } from '@common/components/hint';
import { useFieldContentStyle } from '../content-style.utils';
import { useForceDisabled } from '../../context';

import './number-field.styles.scss';

export const NumberField: React.FC<NumberFieldProps> = React.memo((props) => {
  const { datasheetElement } = props;

  const {
    fieldValue,
    headingPlacedHorizontally,
    headingReference,
    headingSize,
    headingPosition,
    handleFieldValueSave,
    handleFieldValueChange,
    inputRef,
    handleTabNavigation,
    isEditable,
  } = useNumberFieldController(props);

  const forceDisabled = useForceDisabled();
  const { formControlStyle } = useFieldContentStyle(datasheetElement.original);

  const fieldWidth = datasheetElement.original.width;
  const fieldHeight = datasheetElement.original.height;

  const headingAlignmentStyle = getHeadingAlignmentStyle({
    headingPosition,
    headAlign: datasheetElement.original.headalign,
    fieldWidth,
  });

  const headingPositionOffset = getHeadingPositionOffset({
    headingPosition,
    headAlign: datasheetElement.original.headalign,
    headingSize,
    fieldWidth,
    fieldHeight,
  });

  const fieldIsEmpty = (() => { const v = (datasheetElement.original as any).field?.name ?? (datasheetElement.original as any).field?.value; return v == null || String(v).trim() === ''; })();

  const labelComponent = (
    <DatasheetItemLabel
      reference={headingReference}
      inline={headingPlacedHorizontally}
      withImg={datasheetElement.original.showfieldtype}
      withText={datasheetElement.original.headdisplay}
      text={datasheetElement.original.headtext} 
      img={datasheetElement.original.field.imageURL}
      textCustomization={{
        fontSize: datasheetElement.original.headfontsize,
        fontFamily: datasheetElement.original.headfont,
        color: datasheetElement.original.headcolor,
        align: datasheetElement.original.headalign,
        fontStyles: datasheetElement.original.headstyle,
      }}
      absolutePosition={true}
      alignmentStyle={headingAlignmentStyle}
      positionOffset={headingPositionOffset}
    />
  );

  return (
    <DatasheetItem
      style={{
        top: datasheetElement.coordinates.y,
        left: datasheetElement.coordinates.x,
      }}
    >
      {labelComponent}
      <Flex alignItems="center" style={{ position: 'relative' }}>
        <FieldContainer
          fieldId={datasheetElement.original.field.fieldid ?? datasheetElement.original.field.id}
          width={fieldWidth}
          height={fieldHeight}
          showBorder={datasheetElement.original.showborder}
          required={datasheetElement.original.required}
          isEmpty={fieldIsEmpty}
        >
          <FormControl
            inputRef={inputRef}
            onKeyDown={isEditable ? handleTabNavigation : undefined}
            value={forceDisabled ? '' : fieldValue}
            onBlur={isEditable ? handleFieldValueSave : undefined}
            onChange={isEditable ? handleFieldValueChange : undefined}
            disabled={!isEditable}
            style={formControlStyle}
          />
        </FieldContainer>
        {datasheetElement.original.helptext && (
          <Hint description={datasheetElement.original.helptext} />
        )}
      </Flex>
    </DatasheetItem>
  );
});
